import {
  clampPortalBookSequentialAdvanceIdx,
  clampSequentialPlanIdx,
  shouldAcceptPortalBookResumePlanIdx
} from '@/helpers/workerPlaybackPlan'

export type VBufferRemountBootstrapAction
  = | { kind: 'cold_init' }
    | { kind: 'remount_refill_worker', resumePlanIdx: number }
    | { kind: 'remount_rephase', resumePlanIdx: number }
    | { kind: 'remount_preserve_frame0' }

/**
 * Remount branch of {@link initWorker} in VBuffer — decides whether to refill an empty worker,
 * rephase at session authority, or preserve frame 0 without resetting the dwell timer.
 */
export function resolveVBufferRemountBootstrapAction(args: {
  initGeneration: number
  sessionAuthority: number
  workerQueueLen: number
  materializedQueueLen: number
}): VBufferRemountBootstrapAction {
  const { sessionAuthority, workerQueueLen, materializedQueueLen } = args
  if (sessionAuthority < 0) {
    return { kind: 'cold_init' }
  }
  if (workerQueueLen === 0 && materializedQueueLen > 0) {
    return { kind: 'remount_refill_worker', resumePlanIdx: sessionAuthority }
  }
  if (workerQueueLen > 0 && sessionAuthority > 0) {
    return { kind: 'remount_rephase', resumePlanIdx: sessionAuthority }
  }
  if (workerQueueLen > 0 && sessionAuthority === 0) {
    return { kind: 'remount_preserve_frame0' }
  }
  return { kind: 'cold_init' }
}

/** Clamp worker notified idx before buffer-only segment apply (operator master + portal book). */
export function correctBufferOnlyNotifiedIdx(
  lastLoggedPlanIdx: number,
  notifiedIdx: number,
  planLen: number,
  isPortalBookPlan: boolean
): number {
  if (lastLoggedPlanIdx < 0 || notifiedIdx < 0 || planLen <= 1) {
    return notifiedIdx
  }
  return isPortalBookPlan
    ? clampPortalBookSequentialAdvanceIdx(lastLoggedPlanIdx, notifiedIdx, planLen)
    : clampSequentialPlanIdx(lastLoggedPlanIdx, notifiedIdx, planLen)
}

/** Clamp on presentation commit (uses peak presentation authority). */
export function correctPresentationCommitNotifiedIdx(
  presentationAuthorityIdx: number,
  notifiedIdx: number,
  planLen: number,
  isPortalBookPlan: boolean
): number {
  if (presentationAuthorityIdx < 0 || notifiedIdx < 0 || planLen <= 1) {
    return notifiedIdx
  }
  return isPortalBookPlan
    ? clampPortalBookSequentialAdvanceIdx(presentationAuthorityIdx, notifiedIdx, planLen)
    : clampSequentialPlanIdx(presentationAuthorityIdx, notifiedIdx, planLen)
}

/** Whether initWorker may apply the worker's initial notifiedIdx to session authority. */
export function shouldApplyInitialWorkerNotifiedIdx(args: {
  currentAuthority: number
  initNotifiedIdx: number
  planLen: number
  isPortalBookPlan: boolean
}): boolean {
  const { currentAuthority, initNotifiedIdx, planLen, isPortalBookPlan } = args
  if (currentAuthority < 0 || planLen <= 1) {
    return true
  }
  if (isPortalBookPlan) {
    return shouldAcceptPortalBookResumePlanIdx(currentAuthority, initNotifiedIdx, planLen)
  }
  return initNotifiedIdx >= currentAuthority
}
